import { useEffect, useCallback, useRef, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: string;
  width?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({ open, onClose, title, icon, width = '480px', children, footer }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleEsc = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [open, handleEsc]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="modal-overlay"
    >
      <div
        ref={panelRef}
        className="modal-panel"
        style={{ maxWidth: width, maxHeight: 'calc(100vh - 48px)' }}
      >
        {/* Header */}
        <div className="modal-header px-6 py-5 shrink-0">
          <div className="flex items-center gap-2.5">
            {icon && (
              <span className="material-symbols-outlined text-[22px] text-text-secondary">
                {icon}
              </span>
            )}
            <h3 className="text-base font-bold text-text-primary tracking-tight m-0">
              {title}
            </h3>
          </div>
          <button onClick={onClose} className="btn-icon">
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="modal-footer px-6 py-4 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
